import { observer } from 'mobx-react-lite'
import { Fragment, type ReactElement } from 'react'
import { groupBy } from 'lodash'
import { Divider, type IIconProps, Scrollbar } from 'shared/ui'
import { truthy } from 'shared/lib'
import { FlowDropdownButton, FlowDropdownLabel } from 'widgets/Flow'
import { useSmartCallFlowBuilderContext } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/context'
import {
  CallQueueStore,
  ForwardCallStore,
  IVRStore,
  PlayAudioStore,
  RingStore,
  SendSMSStore,
  SendToVoicemailStore,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions'
import { type ISmartCallFlowActionType } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/type'

type IAddActionDropdownProps = {
  setOpen: (value: boolean) => void
  source: string
  target: string
  actions: ISmartCallFlowActionType[]
}

type IAction = {
  type: ISmartCallFlowActionType
  group: string
  element: ReactElement
}

type IMakeActionProps = {
  type: IAction['type']
  group: IAction['group']
  iconProps: IIconProps
  title: string
  onClick: () => void
}

export const AddActionDropdownContent = observer(
  ({ source, target, setOpen, actions }: IAddActionDropdownProps) => {
    const { addActionByType } = useSmartCallFlowBuilderContext()

    const handleClick = (handler: () => void) => () => {
      handler()
      setOpen(false)
    }

    const makeAction = ({ type, group, iconProps, title, onClick }: IMakeActionProps): IAction => ({
      type: type,
      group: group,
      element: <FlowDropdownButton iconProps={iconProps} text={title} onClick={onClick} />,
    })

    const allActionsMap: Record<IAction['type'], IMakeActionProps | null> = {
      incomingCall: null,
      callEnds: null,
      callingFlow: {
        type: 'callingFlow',
        group: 'Actions',
        title: RingStore.title,
        iconProps: RingStore.iconProps,
        onClick: handleClick(() => addActionByType('callingFlow', source, target)),
      },
      callQueue: {
        type: 'callQueue',
        group: 'Actions',
        title: CallQueueStore.title,
        iconProps: CallQueueStore.iconProps,
        onClick: handleClick(() => addActionByType('callQueue', source, target)),
      },
      sendVoicemail: {
        type: 'sendVoicemail',
        group: 'Actions',
        title: SendToVoicemailStore.title,
        iconProps: SendToVoicemailStore.iconProps,
        onClick: handleClick(() => addActionByType('sendVoicemail', source, target)),
      },
      playSound: {
        type: 'playSound',
        group: 'Actions',
        title: PlayAudioStore.title,
        iconProps: PlayAudioStore.iconProps,
        onClick: handleClick(() => addActionByType('playSound', source, target)),
      },
      ivrPhoneTree: {
        type: 'ivrPhoneTree',
        group: 'Actions',
        title: IVRStore.title,
        iconProps: IVRStore.iconProps,
        onClick: handleClick(() => addActionByType('ivrPhoneTree', source, target)),
      },
      forwardCall: {
        type: 'forwardCall',
        group: 'Actions',
        title: ForwardCallStore.title,
        iconProps: ForwardCallStore.iconProps,
        onClick: handleClick(() => addActionByType('forwardCall', source, target)),
      },
      sendMessage: {
        type: 'sendMessage',
        group: 'Actions',
        title: SendSMSStore.title,
        iconProps: SendSMSStore.iconProps,
        onClick: handleClick(() => addActionByType('sendMessage', source, target)),
      },
      businessHours: {
        type: 'businessHours',
        group: 'Logic',
        title: 'Business hours',
        iconProps: {
          icon: 'clock',
          color: 'var(--custom-green-custom)',
        },
        onClick: handleClick(() => console.log('businessHours')),
      },
      aiTextingAgent: {
        type: 'aiTextingAgent',
        group: 'AI',
        title: 'AI Texting Agent',
        iconProps: {
          icon: 'stars',
          color: 'var(--purple-70)',
        },
        onClick: handleClick(() => console.log('AI Texting Agent')),
      },
      aiCallingAgent: {
        type: 'aiCallingAgent',
        group: 'AI',
        title: 'AI Calling Agent',
        iconProps: {
          icon: 'stars',
          color: 'var(--purple-70)',
        },
        onClick: handleClick(() => console.log('AI Calling Agent')),
      },
    }

    const actionsItems = actions
      .map((type) => allActionsMap[type] && makeAction(allActionsMap[type]))
      .filter(truthy)

    return (
      <Scrollbar autoHeight autoHeightMax={440}>
        {Object.entries(groupBy(actionsItems, 'group')).map(([group, values], index) => (
          <Fragment key={group}>
            {index !== 0 && <Divider sx={{ marginBlock: '5.5px' }} />}
            <FlowDropdownLabel ariaLabel={group}>{group}</FlowDropdownLabel>
            {values.map((value) => (
              <Fragment key={value.type}>{value.element}</Fragment>
            ))}
          </Fragment>
        ))}
      </Scrollbar>
    )
  }
)
